"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Aitools() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "Neural Shield",
    description: "Advanced AI-driven threat detection that identifies and neutralizes vulnerabilities in real-time.",
    tensile: "99.9% Uptime",
    temp: "24/7 Active",
    Icon: Layers
  }, {
    name: "Predictive Logic",
    description: "Machine learning algorithms engineered to forecast network traffic patterns and optimize bandwidth.",
    tensile: "Ultra Fast",
    temp: "Low Latency",
    Icon: Droplet
  }, {
    name: "Data Encryption",
    description: "Military-grade cryptographic protocols ensuring your digital footprint remains invisible to trackers.",
    tensile: "AES-256",
    temp: "Zero Logs",
    Icon: Beaker
  }, {
    name: "Smart Tunneling",
    description: "Dynamic routing technology that automatically selects the fastest server path for your connection.",
    tensile: "Global Reach",
    temp: "Auto-Switch",
    Icon: Box
  }, {
    name: "Adaptive Privacy",
    description: "Flexible security layers that adjust to your browsing habits for maximum anonymity and speed.",
    tensile: "High Elongation",
    temp: "Seamless",
    Icon: Atom
  }, {
    name: "Quantum Defense",
    description: "Next-generation security architecture delivering superior protection against emerging cyber threats.",
    tensile: "Hardened",
    temp: "Future-Proof",
    Icon: Shield
  }, {
    name: "Traffic Analysis",
    description: "High-resolution monitoring tools for detailed insights into your network performance and security.",
    tensile: "Real-Time",
    temp: "Detailed",
    Icon: Cpu
  }, {
    name: "Core Sovereignty",
    description: "High-performance privacy engine with exceptional thermal and operational stability for demanding users.",
    tensile: "Max Power",
    temp: "Optimized",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="ai-tools" className="dark bg-background text-foreground py-24 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.15em] uppercase mb-6" style={{
            color: "var(--primary)"
          }}>
              AI Intelligence Suite
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] tracking-tighter text-foreground">
              Engineered for digital sovereignty.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              From adaptive threat detection to quantum-ready encryption, our AI-driven toolset
              provides the precision, speed, and security that modern digital environments demand.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md">
                {}
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300 bg-muted">
                  <Icon className="h-6 w-6 transition-colors duration-300" strokeWidth={1.5} style={{
                color: "var(--primary)"
              }} />
                </div>

                {}
                <h3 className="font-default font-bold text-[28px] leading-tight text-card-foreground mb-3">
                  {material.name}
                </h3>

                {}
                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {material.description}
                </p>

                {}
                <div className="pt-5 border-t border-border">
                  <p className="font-mono text-xs tracking-wider uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--primary)"
                }}>STATUS:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--primary)"
                }}>MODE:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}